import { useMemo, useState } from 'react';
import { UserCheck, Smartphone } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { STATE_META, stateStyle, urgencyRank, elapsed, fuseGroups, displayName, people, phones, StateIcon } from './tableStates';

const SECTIONS = [[0, 'Llamados'], [1, 'Pedidos listos'], [2, 'Cuenta pedida'], [3, 'Resto']];

const sinceMin = (iso, now) => (iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000)) : null);

const Row = ({ u, now, onSelect }) => {
  const m = STATE_META[u.state];
  const sub = u.state === 'waiter_call' ? `Llamó hace ${sinceMin(u.call_since, now)} min`
    : u.state === 'order_ready' ? `${u.ready_count} pedido(s) listo(s)`
    : u.state === 'reserved' ? `Reserva ${u.reservation?.time} · ${u.reservation?.customer_name}` : m.label;
  return (
    <button type="button" onClick={() => onSelect(u)} data-testid={`waiter-row-${u.unit_id}`} data-state={u.state}
      className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card text-left active:scale-[.99] transition-transform">
      <div className={`w-12 h-12 rounded-xl grid place-items-center font-heading font-bold text-sm shrink-0 ${m.blink ? 'table-blink' : ''}`} style={stateStyle(u.state)} data-state-swatch>
        <StateIcon state={u.state} />{displayName(u).replace(/^Mesa\s*/i, '').slice(0, 7)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{sub}</div>
        <div className="text-xs text-muted-foreground truncate">
          {u.session_id ? `${people(u.guest_count)} · ${elapsed(u.opened_at, now)}${u.waiter_name ? ` · ${u.waiter_name}` : ''}` : displayName(u)}
        </div>
      </div>
      {u.session_id && (
        <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground shrink-0" data-testid={`waiter-row-devices-${u.unit_id}`} title={phones(u.devices || 0)}>
          <Smartphone className="w-3.5 h-3.5" />{u.devices || 0}
        </span>
      )}
    </button>
  );
};

export const WaiterTableList = ({ units, userId, now, onSelect, defaultMine = true }) => {
  const [mine, setMine] = useState(defaultMine);
  const list = useMemo(() => fuseGroups(units)
    .filter(u => !mine || u.waiter_id === userId || (!u.waiter_id && (urgencyRank(u.state) < 3 || ['to_clean', 'reserved'].includes(u.state))))
    .filter(u => u.state !== 'free')
    .sort((a, b) => urgencyRank(a.state) - urgencyRank(b.state) || (a.call_since || a.opened_at || '').localeCompare(b.call_since || b.opened_at || '')),
  [units, mine, userId]);
  return (
    <div className="space-y-3" data-testid="waiter-table-list">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold">{mine ? 'Mis mesas' : 'Todas las mesas'}</h2>
        <label className="flex items-center gap-2 text-xs font-bold bg-primary text-primary-foreground rounded-full px-3 py-1.5">
          <UserCheck className="w-4 h-4" />Mis mesas
          <Switch checked={mine} onCheckedChange={setMine} data-testid="waiter-mine-toggle" />
        </label>
      </div>
      {list.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="waiter-list-empty">Sin mesas que atender</p>}
      {SECTIONS.map(([rank, title]) => {
        const items = list.filter(u => urgencyRank(u.state) === rank);
        if (!items.length) return null;
        return (
          <div key={rank} className="space-y-2" data-testid={`waiter-section-${rank}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
            {items.map(u => <Row key={u.unit_id} u={u} now={now} onSelect={onSelect} />)}
          </div>
        );
      })}
    </div>
  );
};
